import { SectionType } from '@/components/decision-tree';
import {
  MultipleChoiceQuestion,
  NumericQuestion,
  SingleChoiceQuestion,
  WeightAnswer,
  WeightQuestion,
} from '@/components/decision-tree/components/SurveyItem/components/Question/questionTypes';
import { displayDateFormat } from '@/constants/dates';
import { useGetQuestionnaire } from '@/queries/questionnaire.queries';
import { useDisclosure } from '@cmpsr/components';
import { format } from 'date-fns';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type SectionData = {
  surveyItemId: string;
  label: string;
  answerText: string | undefined;
  answerDate: string;
  isIDontKnowAnswer: boolean;
};

const isSectionData = (data: SectionData | undefined): data is SectionData => data !== undefined;

export default function useRiskAssessmentSectionsController() {
  const {
    isOpen: showModalEditAnswer,
    onOpen: onShowModalEditAnswer,
    onClose: onHideModalEditAnswer,
  } = useDisclosure();
  const { data: questionnaireData } = useGetQuestionnaire();
  const riskAssessmentSections = questionnaireData?.questionnaire?.sections;
  const riskAssessmentAnswers = questionnaireData?.answers;
  const [searchParams, setSearchParams] = useSearchParams();

  const getSectionUnclearAnswers = (sectionId: string) => {
    const sectionIDKAnswers = riskAssessmentAnswers?.filter(
      (answer) => answer.sectionId === sectionId && answer.answer?.type === 'iDontKnow'
    );
    return sectionIDKAnswers?.length ?? 0;
  };

  const getSectionData = (section: SectionType): Array<SectionData> => {
    return section.surveyItems
      .map((surveyItem) => {
        if (surveyItem.surveyItemType === 'interstitial') return undefined;

        const answer = riskAssessmentAnswers?.find(
          (answer) => answer.surveyItemId === surveyItem.id && answer.sectionId === section.id
        );

        if (!answer) return undefined;

        let answerText;
        let isIDontKnowAnswer = false;

        switch (answer?.answer?.type) {
          case 'singleChoice':
            {
              const { value } = answer.answer;
              const singleChoiceQuestion = surveyItem as SingleChoiceQuestion;
              const singleChoiceOption = singleChoiceQuestion.options.find((option) => option.id === value);
              answerText = singleChoiceOption?.label;
            }
            break;
          case 'multipleChoice':
            {
              const { values } = answer.answer;
              const multipleChoiceQuestion = surveyItem as MultipleChoiceQuestion;
              const selectedOptions = values.map((value) => {
                const option = multipleChoiceQuestion.options.find((option) => option.id === value);
                return option?.label;
              });

              const noneOption = multipleChoiceQuestion.options.find((option) => option.type === 'none');
              if (selectedOptions.length === 0 && noneOption) {
                selectedOptions.push(noneOption.label);
              }

              answerText = selectedOptions.join(', ');
            }
            break;
          case 'number':
            {
              const numericQuestion = surveyItem as NumericQuestion;
              answerText = `${answer.answer.value} ${numericQuestion.trailingMask}`;
            }
            break;
          case 'height':
            answerText = `${answer.answer.feet}ft ${answer.answer.inches}in`;
            break;
          case 'date':
            answerText = answer.answer.value;
            break;
          case 'weight':
            {
              const weightQuestion = surveyItem as WeightQuestion;
              const { pounds, ounces } = answer.answer as WeightAnswer;
              const poundsText = `${pounds} ${weightQuestion.pounds.trailingMask}`;
              const ouncesText = `${ounces} ${weightQuestion.ounces.trailingMask}`;
              answerText = `${poundsText} ${ouncesText}`;
            }
            break;
          case 'iDontKnow':
            answerText = "I'm not sure right now";
            isIDontKnowAnswer = true;
            break;
        }

        const answerDate = answer?.answeredAt !== undefined ? format(answer.answeredAt, displayDateFormat) : '';

        return {
          surveyItemId: surveyItem.id,
          label: surveyItem.label,
          answerText,
          answerDate,
          isIDontKnowAnswer,
        };
      })
      .filter(isSectionData);
  };

  const getBadgeStatus = (unclearAnswers: number | undefined): 'complete' | 'inaccurate' | undefined => {
    if (unclearAnswers === undefined) return;

    return unclearAnswers === 0 ? 'complete' : 'inaccurate';
  };

  const getBadgeText = (unclearAnswers: number | undefined): string | undefined => {
    if (unclearAnswers === undefined) return;

    return unclearAnswers === 0 ? '100% complete' : `${unclearAnswers} unclear answers`;
  };

  const sectionIcons = ['demographics', 'Diet-General', 'occupation-alt', 'Clinical-Dr', 'biomarkers-alt-1'];

  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);

  const handleEditClick = (surveyItemId: string) => {
    setSelectedQuestionId(surveyItemId);
    onShowModalEditAnswer();
  };

  const handleCloseModal = () => {
    setSelectedQuestionId(null);
    searchParams.delete('surveyItemId');
    setSearchParams(searchParams);
    onHideModalEditAnswer();
  };

  return {
    riskAssessmentSections,
    getSectionData,
    getBadgeStatus,
    getBadgeText,
    sectionIcons,
    getSectionUnclearAnswers,
    showModalEditAnswer,
    onHideModalEditAnswer: handleCloseModal,
    selectedQuestionId,
    handleEditClick,
  };
}
